// pages/CheckoutPage.jsx - Updated with Confirmation Timer Modal
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Container, Paper, Typography, TextField, 
  Button, Divider, List, ListItem, 
  ListItemText, Radio, RadioGroup, 
  FormControlLabel, FormLabel, Box,
  Chip, Checkbox, Alert, Grid,
  Dialog, DialogTitle, DialogContent,
  DialogActions, Accordion, AccordionSummary,
  AccordionDetails, CircularProgress,
  IconButton, Collapse, InputAdornment,
  AppBar, Toolbar, Fab, Zoom, LinearProgress,
  Backdrop, Fade
} from '@mui/material';
import { LocationOn } from '@mui/icons-material';
import { 
  NoteAdd as NoteAddIcon, Close as CloseIcon,
  LocalOffer, Savings, Loyalty, 
  Info as InfoIcon, CreditCard as CreditCardIcon,
  Lock as LockIcon, Security as SecurityIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon, Payment as PaymentIcon, Phone as PhoneIcon,
  Timer as TimerIcon, Warning as WarningIcon, 
  ShoppingBag as ShoppingBagIcon, ConfirmationNumber as ConfirmationNumberIcon
} from '@mui/icons-material';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement
} from '@stripe/react-stripe-js';
import noPhoto from "../assets/no_photo.webp";
import GoogleMapModal from "../components/GoogleMapModal";
import MyLocationIcon from '@mui/icons-material/MyLocation';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
// Confirmation Timer Modal Component with Lottie Animations
import Lottie from 'lottie-react';
// Import your Lottie JSON files (place them in your assets folder)
import shoppingCartAnimation from '../assets/shoping_card.json';
import checkMarkAnimation from '../assets/success.json';
import loadingAnimation from '../assets/yallow_loading_dot.json';
import warningAnimation from '../assets/warning.json';
import timerAnimation from '../assets/dead_line_loading.json';
import { formatPrice } from '../components/CarrancyTrans';

// RETO Cafe Black & Yellow Theme Styles
const styles = {
  actionButton: {
    backgroundColor: '#FFD700',
    color: '#000000',
    borderRadius: '20px',
    px: 3,
    py: 1,
    textTransform: 'none',
    fontWeight: 700,
    marginTop: 5,
    '&:hover': {
      backgroundColor: '#FFE55C',
      boxShadow: '0 4px 12px rgba(255, 215, 0, 0.3)',
    }
  },
  lcathionactionButton: {
    backgroundColor: 'transparent',
    color: '#FFD700',
    border: '1.5px solid #FFD700',
    borderRadius: '40px',
    px: 1,
    py: 1,
    textTransform: 'none',
    fontWeight: 600,
    minWidth: '48px',
    '&:hover': {
      backgroundColor: '#FFD700',
      color: '#000000',
      borderColor: '#FFD700',
    }
  }
};

const ConfirmationTimerModal = ({ 
  open, 
  onClose, 
  onConfirm, 
  itemsCount, 
  totalAmount, 
  language, 
  carancyTxt,
  savingsAmount,
  pointsDiscount,
  cartItems = [], // New prop for cart items
  getItemPrice, // New prop to get item price
  getOriginalPrice, // New prop to get original price
  hasDiscount // New prop to check discount
}) => {
  const [timeLeft, setTimeLeft] = useState(10);
  const [isCounting, setIsCounting] = useState(true);
  const [autoConfirmed, setAutoConfirmed] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [expandedItems, setExpandedItems] = useState({});

  // Auto confirm when time reaches 0
  useEffect(() => {
    if (open && timeLeft === 0 && !autoConfirmed && !isConfirming) {
      console.log('Auto-confirm triggered!');
      setAutoConfirmed(true);
      setIsConfirming(true);
      setIsCounting(false);
      
      // Call onConfirm immediately when timer reaches 0
      onConfirm();
    }
  }, [open, timeLeft, autoConfirmed, isConfirming, onConfirm]);

  useEffect(() => {
    if (open && isCounting) {
      const timer = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            setIsCounting(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
      return () => clearInterval(timer);
    }
  }, [open, isCounting]);

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setTimeLeft(10);
      setIsCounting(true);
      setAutoConfirmed(false);
      setIsConfirming(false);
      setExpandedItems({});
    }
  }, [open]);

  const handleConfirm = () => {
    if (timeLeft === 0 || isConfirming) return;
    setIsConfirming(true);
    setIsCounting(false);
    onConfirm();
  };

  const handleCancel = () => {
    if (isConfirming) return;
    setIsCounting(false);
    onClose();
  };

  const toggleItemExpand = (itemId) => {
    setExpandedItems(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  const progressPercentage = (timeLeft / 10) * 100;
  const isExpired = timeLeft === 0;

  // Animation style
  const animationStyle = {
    width: 100,
    height: 100,
    margin: '0 auto',
  };

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: '#0D0D0D',
          border: '2px solid #FFD700',
          borderRadius: '24px',
          overflow: 'hidden',
          animation: 'pulse 0.5s ease-in-out',
          '@keyframes pulse': {
            '0%': { transform: 'scale(0.95)', opacity: 0 },
            '100%': { transform: 'scale(1)', opacity: 1 },
          }
        }
      }}
    >
      {/* Header with Timer */}
      <Box sx={{ 
        background: isExpired || isConfirming
          ? 'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)'
          : 'linear-gradient(135deg, #FFD700 0%, #FFC107 100%)',
        p: 3,
        textAlign: 'center',
        position: 'relative',
        transition: 'background 0.3s ease'
      }}>
        {/* Timer Badge */}
        <Box sx={{ 
          position: 'absolute',
          top: 16,
          right: 16,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          backgroundColor: 'rgba(0,0,0,0.3)',
          borderRadius: '30px',
          px: 1.5,
          py: 0.5,
          zIndex: 1
        }}>
          <Box sx={{ width: 24, height: 24 }}>
            <Lottie 
              animationData={timerAnimation} 
              loop={true} 
              autoplay={!isExpired && !isConfirming}
              style={{ width: 24, height: 24 }}
            />
          </Box>
          <Typography variant="h6" sx={{ color: '#000000', fontWeight: 800 }}>
            {timeLeft}s
          </Typography>
        </Box>
        
        {/* Main Animation */}
        {isConfirming ? (
          <>
            <Box sx={animationStyle}>
              <Lottie 
                animationData={loadingAnimation} 
                loop={true} 
                autoplay={true}
              />
            </Box>
            <Typography variant="h4" sx={{ color: '#000000', fontWeight: 800, mt: 2 }}>
              {language === 'ar' ? 'جاري تأكيد الطلب...' : 'Confirming Order...'}
            </Typography>
            <Typography variant="body2" sx={{ color: '#000000', opacity: 0.8 }}>
              {language === 'ar' 
                ? 'يرجى الانتظار...'
                : 'Please wait...'}
            </Typography>
          </>
        ) : isExpired ? (
          <>
            <Box sx={animationStyle}>
              <Lottie 
                animationData={checkMarkAnimation} 
                loop={false} 
                autoplay={true}
              />
            </Box>
            <Typography variant="h4" sx={{ color: '#000000', fontWeight: 800, mt: 2 }}>
              {language === 'ar' ? 'جاري التأكيد...' : 'Confirming...'}
            </Typography>
            <Typography variant="body2" sx={{ color: '#000000', opacity: 0.8 }}>
              {language === 'ar' 
                ? 'سيتم تأكيد الطلب تلقائياً'
                : 'Order will be confirmed automatically'}
            </Typography>
          </>
        ) : (
          <>
            <Box sx={animationStyle}>
              <Lottie 
                animationData={shoppingCartAnimation} 
                loop={true} 
                autoplay={true}
              />
            </Box>
            <Typography variant="h4" sx={{ color: '#000000', fontWeight: 800, mt: 2 }}>
              {language === 'ar' ? 'تأكيد الطلب' : 'Confirm Order'}
            </Typography>
            <Typography variant="body2" sx={{ color: '#000000', opacity: 0.8 }}>
              {language === 'ar' 
                ? 'يرجى مراجعة طلبك قبل التأكيد'
                : 'Please review your order before confirming'}
            </Typography>
          </>
        )}
      </Box>

      {/* Timer Progress Bar */}
      <LinearProgress
        variant="determinate"
        value={progressPercentage}
        sx={{
          height: 4,
          backgroundColor: '#2D2D2D',
          '& .MuiLinearProgress-bar': {
            backgroundColor: isExpired || isConfirming ? '#4CAF50' : '#FFD700',
            transition: 'width 1s linear'
          }
        }}
      />

      <DialogContent sx={{ p: 3, maxHeight: '60vh', overflowY: 'auto' }}>
        {/* Order Summary Card */}
        <Paper sx={{ 
          p: 2, 
          mb: 3, 
          backgroundColor: '#1A1A1A', 
          borderRadius: '16px',
          border: '1px solid #2D2D2D',
          opacity: isConfirming ? 0.7 : 1,
          animation: 'fadeInUp 0.5s ease-out',
          '@keyframes fadeInUp': {
            '0%': { opacity: 0, transform: 'translateY(20px)' },
            '100%': { opacity: 1, transform: 'translateY(0)' },
          }
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <ConfirmationNumberIcon sx={{ color: '#FFD700' }} />
            <Typography variant="subtitle1" sx={{ color: '#FFD700', fontWeight: 700 }}>
              {language === 'ar' ? 'ملخص الطلب' : 'Order Summary'}
            </Typography>
          </Box>

          {/* Detailed Items List */}
          {cartItems.length > 0 && (
            <Box sx={{ 
              maxHeight: '300px', 
              overflowY: 'auto',
              mb: 2,
              '&::-webkit-scrollbar': {
                width: '4px',
              },
              '&::-webkit-scrollbar-track': {
                background: '#2D2D2D',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: '#FFD700',
                borderRadius: '4px',
              },
            }}>
              {cartItems.map((item, index) => {
                const currentPrice = getItemPrice?.(item) || item.price || 0;
                const originalPrice = getOriginalPrice?.(item) || item.price || 0;
                const isDiscounted = hasDiscount?.(item) || false;
                const discountPercent = isDiscounted 
                  ? Math.round((1 - currentPrice / originalPrice) * 100)
                  : 0;
                const itemNote = item.notes || '';
                const isExpanded = expandedItems[item.id] || false;

                return (
                  <Box 
                    key={item.id} 
                    sx={{ 
                      mb: 1.5,
                      animation: `slideInRight ${0.3 + index * 0.05}s ease-out`,
                      '@keyframes slideInRight': {
                        '0%': { opacity: 0, transform: 'translateX(20px)' },
                        '100%': { opacity: 1, transform: 'translateX(0)' },
                      }
                    }}
                  >
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'space-between',
                      p: 1.5,
                      borderRadius: '12px',
                      backgroundColor: '#0D0D0D',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        backgroundColor: '#1A1A1A',
                      }
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
                        {/* Quantity Badge */}
                        <Box sx={{
                          minWidth: 32,
                          height: 32,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: '#FFD700',
                          borderRadius: '8px',
                        }}>
                          <Typography variant="body2" sx={{ color: '#000000', fontWeight: 800 }}>
                            {item.quantity}
                          </Typography>
                        </Box>

                        {/* Item Name */}
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" sx={{ color: '#FFFFFF', fontWeight: 600 }}>
                            {language === 'ar' ? item.product?.name : item.product?.name_en}
                          </Typography>
                          
                          {/* Size and Extras Summary */}
                          {(item.selected_size || (item.selected_extras?.length > 0)) && (
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                color: '#FFD700', 
                                display: 'block',
                                cursor: 'pointer',
                                '&:hover': { textDecoration: 'underline' }
                              }}
                              onClick={() => toggleItemExpand(item.id)}
                            >
                              {isExpanded ? '▲ ' : '▼ '}
                              {language === 'ar' ? 'تفاصيل التخصيص' : 'Customization details'}
                            </Typography>
                          )}
                          
                          {/* Note Indicator */}
                          {itemNote && (
                            <Typography variant="caption" sx={{ color: '#FF9800', display: 'block' }}>
                              📝 {itemNote.length > 30 ? itemNote.substring(0, 30) + '...' : itemNote}
                            </Typography>
                          )}
                        </Box>

                        {/* Price */}
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography variant="body2" sx={{ color: '#FFD700', fontWeight: 700 }}>
                            {(currentPrice * item.quantity).toFixed(2)} {carancyTxt}
                          </Typography>
                          {isDiscounted && (
                            <Typography variant="caption" sx={{ color: '#B3B3B3', textDecoration: 'line-through', display: 'block' }}>
                              {(originalPrice * item.quantity).toFixed(2)} {carancyTxt}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Box>

                    {/* Expanded Customization Details */}
                    {(item.selected_size || (item.selected_extras?.length > 0)) && (
                      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        <Box sx={{ 
                          p: 1.5, 
                          pl: 7, 
                          mt: 0.5,
                          backgroundColor: '#0D0D0D',
                          borderRadius: '8px',
                          borderLeft: '2px solid #FFD700'
                        }}>
                          {item.selected_size && (
                            <Typography variant="caption" sx={{ color: '#B3B3B3', display: 'block' }}>
                              {language === 'ar' ? 'المقاس: ' : 'Size: '}
                              <span style={{ color: '#FFD700' }}>
                                {language === 'ar' ? item.selected_size.name_ar || item.selected_size.name : item.selected_size.name_en}
                                {item.selected_size.price > 0 && ` (+${item.selected_size.price} ${carancyTxt})`}
                              </span>
                            </Typography>
                          )}
                          {item.selected_extras?.length > 0 && (
                            <Typography variant="caption" sx={{ color: '#B3B3B3', display: 'block', mt: 0.5 }}>
                              {language === 'ar' ? 'الإضافات: ' : 'Extras: '}
                              {item.selected_extras.map((extra, idx) => (
                                <span key={extra.id}>
                                  <span style={{ color: '#FFD700' }}>
                                    {language === 'ar' ? extra.name_ar || extra.name : extra.name_en}
                                    {extra.price > 0 && ` (+${extra.price} ${carancyTxt})`}
                                  </span>
                                  {idx < item.selected_extras.length - 1 && ', '}
                                </span>
                              ))}
                            </Typography>
                          )}
                          {itemNote && (
                            <Typography variant="caption" sx={{ color: '#FF9800', display: 'block', mt: 0.5 }}>
                              📝 {language === 'ar' ? 'ملاحظة: ' : 'Note: '} {itemNote}
                            </Typography>
                          )}
                        </Box>
                      </Collapse>
                    )}
                  </Box>
                );
              })}
            </Box>
          )}

          <Divider sx={{ my: 1.5, backgroundColor: '#2D2D2D' }} />

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
            <Typography sx={{ color: '#B3B3B3' }}>
              {language === 'ar' ? 'عدد المنتجات' : 'Items'}
            </Typography>
            <Typography sx={{ color: '#FFFFFF', fontWeight: 600 }}>
              {itemsCount} {language === 'ar' ? 'منتج' : 'items'}
            </Typography>
          </Box>

          {(savingsAmount > 0 || pointsDiscount > 0) && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
              <Typography sx={{ color: '#4CAF50' }}>
                {language === 'ar' ? 'الخصم الإجمالي' : 'Total Savings'}
              </Typography>
              <Typography sx={{ color: '#4CAF50', fontWeight: 600 }}>
                - {(savingsAmount + pointsDiscount).toFixed(2)} {carancyTxt}
              </Typography>
            </Box>
          )}

          <Divider sx={{ my: 1.5, backgroundColor: '#2D2D2D' }} />

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ color: '#FFFFFF', fontWeight: 700 }}>
              {language === 'ar' ? 'الإجمالي' : 'Total'}
            </Typography>
            <Typography variant="h5" sx={{ color: '#FFD700', fontWeight: 800 }}>
              {totalAmount.toFixed(2)} {carancyTxt}
            </Typography>
          </Box>
        </Paper>

        {/* Warning Message with Animation */}
        {!isConfirming && !isExpired && (
          <Alert 
            severity="warning"
            icon={
              <Box sx={{ width: 24, height: 24 }}>
                <Lottie 
                  animationData={warningAnimation} 
                  loop={true} 
                  autoplay={true}
                  style={{ width: 24, height: 24 }}
                />
              </Box>
            }
            sx={{ 
              mb: 2,
              backgroundColor: 'rgba(255, 152, 0, 0.1)',
              color: '#FF9800',
              border: '1px solid #FF9800',
              borderRadius: '12px',
              animation: 'fadeInUp 0.5s ease-out',
            }}
          >
            <Typography variant="body2">
              {language === 'ar' ? 'سيتم إرسال الطلب إلى المطبخ' : 'Order will be sent to kitchen'}
            </Typography>
          </Alert>
        )}

        {/* Auto-confirm info message */}
        {!isConfirming && isExpired && (
          <Alert 
            severity="info"
            icon={
              <Box sx={{ width: 24, height: 24 }}>
                <Lottie 
                  animationData={timerAnimation} 
                  loop={true} 
                  autoplay={true}
                  style={{ width: 24, height: 24 }}
                />
              </Box>
            }
            sx={{ 
              mb: 2,
              backgroundColor: 'rgba(76, 175, 80, 0.1)',
              color: '#4CAF50',
              border: '1px solid #4CAF50',
              borderRadius: '12px',
              animation: 'fadeInUp 0.5s ease-out',
            }}
          >
            <Typography variant="body2">
              {language === 'ar'
                ? '✅ جاري تأكيد الطلب تلقائياً...'
                : '✅ Confirming order automatically...'}
            </Typography>
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0, gap: 2 }}>
        {/* Cancel Button - Hidden when expired or confirming */}
        {!isExpired && !isConfirming && (
          <Button
            fullWidth
            variant="outlined"
            onClick={handleCancel}
            sx={{
              py: 1.5,
              borderRadius: '12px',
              borderColor: '#FF3D00',
              color: '#FF3D00',
              fontWeight: 700,
              '&:hover': {
                borderColor: '#FF3D00',
                backgroundColor: 'rgba(255, 61, 0, 0.1)',
              }
            }}
          >
            {language === 'ar' ? 'إلغاء' : 'Cancel'}
          </Button>
        )}
        
        {/* Confirm Button - Hidden when expired or confirming */}
        {!isExpired && !isConfirming && (
          <Button
            fullWidth
            variant="contained"
            onClick={handleConfirm}
            sx={{
              py: 1.5,
              borderRadius: '12px',
              backgroundColor: '#FFD700',
              color: '#000000',
              fontWeight: 800,
              '&:hover': {
                backgroundColor: '#FFE55C',
              },
            }}
          >
            {language === 'ar' ? `تأكيد (${timeLeft}s)` : `Confirm (${timeLeft}s)`}
          </Button>
        )}
        
        {/* Loading indicator when confirming */}
        {isConfirming && (
          <Box sx={{ 
            width: '100%', 
            textAlign: 'center', 
            py: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1
          }}>
            <CircularProgress size={30} sx={{ color: '#FFD700' }} />
            <Typography variant="body2" sx={{ color: '#B3B3B3' }}>
              {language === 'ar' ? 'جاري تأكيد الطلب...' : 'Confirming order...'}
            </Typography>
          </Box>
        )}
      </DialogActions>
    </Dialog>
  );
};

// Stripe Elements styling
const stripeElementOptions = {
  style: {
    base: {
      fontSize: '16px',
      color: '#FFFFFF',
      '::placeholder': {
        color: '#B3B3B3',
      },
      iconColor: '#FFD700',
    },
    invalid: {
      color: '#FF3D00',
      iconColor: '#FF3D00',
    },
  },
};

// Stripe payment form component
const StripePaymentForm = ({ 
  clientSecret, 
  onSuccess, 
  onError, 
  onCancel,
  language,
  totalAmount,
  currency = 'myr'
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [cardComplete, setCardComplete] = useState({
    cardNumber: false,
    cardExpiry: false,
    cardCvc: false
  });

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);

    const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
      payment_method: {
        card: elements.getElement(CardNumberElement),
        billing_details: {
          name: 'Card Holder',
        },
      }
    });

    if (error) {
      onError(error.message);
    } else if (paymentIntent.status === 'succeeded') {
      onSuccess(paymentIntent);
    }

    setProcessing(false);
  };

  return (
    <form onSubmit={handleSubmit}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="subtitle2" sx={{ color: '#FFD700', mb: 1 }}>
            {language === 'ar' ? 'رقم البطاقة' : 'Card Number'}
          </Typography>
          <Box sx={{
            backgroundColor: '#1A1A1A',
            borderRadius: '8px',
            p: 2,
            border: '1px solid #2D2D2D',
            '&:hover': { borderColor: '#FFD700' }
          }}>
            <CardNumberElement 
              options={{
                ...stripeElementOptions,
                showIcon: true,
                placeholder: '4111 1111 1111 1111'
              }}
              onChange={(e) => setCardComplete({ ...cardComplete, cardNumber: e.complete })}
            />
          </Box>
        </Grid>

        <Grid item xs={6}>
          <Typography variant="subtitle2" sx={{ color: '#FFD700', mb: 1 }}>
            {language === 'ar' ? 'تاريخ الانتهاء' : 'Expiry Date'}
          </Typography>
          <Box sx={{
            backgroundColor: '#1A1A1A',
            borderRadius: '8px',
            p: 2,
            border: '1px solid #2D2D2D',
            '&:hover': { borderColor: '#FFD700' }
          }}>
            <CardExpiryElement 
              options={stripeElementOptions}
              onChange={(e) => setCardComplete({ ...cardComplete, cardExpiry: e.complete })}
            />
          </Box>
        </Grid>

        <Grid item xs={6}>
          <Typography variant="subtitle2" sx={{ color: '#FFD700', mb: 1 }}>
            CVC
          </Typography>
          <Box sx={{
            backgroundColor: '#1A1A1A',
            borderRadius: '8px',
            p: 2,
            border: '1px solid #2D2D2D',
            '&:hover': { borderColor: '#FFD700' }
          }}>
            <CardCvcElement 
              options={stripeElementOptions}
              onChange={(e) => setCardComplete({ ...cardComplete, cardCvc: e.complete })}
            />
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <Button
              fullWidth
              onClick={onCancel}
              sx={{ color: '#B3B3B3' }}
            >
              {language === 'ar' ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button
              fullWidth
              type="submit"
              variant="contained"
              disabled={!cardComplete.cardNumber || !cardComplete.cardExpiry || !cardComplete.cardCvc || processing}
              startIcon={processing ? <CircularProgress size={20} /> : <LockIcon />}
              sx={{
                backgroundColor: '#FFD700',
                color: '#000000',
                '&:hover': { backgroundColor: '#FFE55C' }
              }}
            >
              {processing 
                ? (language === 'ar' ? 'جاري المعالجة...' : 'Processing...')
                : (language === 'ar' ? `دفع ${formatPrice(totalAmount,currency,language)}` : `Pay ${formatPrice(totalAmount,currency,language)}`)
              }
            </Button>
          </Box>
        </Grid>
      </Grid>

      <Alert 
        severity="info"
        icon={<SecurityIcon />}
        sx={{ 
          mt: 2,
          backgroundColor: 'rgba(0, 100, 0, 0.1)',
          color: '#4CAF50',
          border: '1px solid #4CAF50'
        }}
      >
        {language === 'ar' 
          ? 'جميع معلومات الدفع مشفرة وآمنة بواسطة Stripe. لن يتم تخزين بيانات بطاقتك.'
          : 'All payment information is encrypted and secure with Stripe. Your card data is not stored.'
        }
      </Alert>
    </form>
  );
};

// Main checkout page component
const CheckoutPage = ({keywordsData, language}) => {
  const { 
    cart, 
    cartTotal, 
    updateItemNotes,
    totalSavings, 
    clearCart,
    getItemPrice,
    getOriginalPrice,
    hasDiscount,
    deliverFees,
    serviceChargePercent,
    serviceChargeValue,
    cartCurrency
  } = useCart();
  
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // Confirmation Timer Modal State
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [pendingCheckout, setPendingCheckout] = useState(null);
  
  // Refs
  const debounceTimeoutRef = React.useRef(null);
  const contentRef = React.useRef(null);
  
  // Payment related states
  const [paymentMethod, setPaymentMethod] = useState('cash_on_delivery');
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [paymentResult, setPaymentResult] = useState(null);
  const [isPhoneVerified, setIsPhoneVerified] = useState(user?.is_phone_verified || false);
  const [showVerification, setShowVerification] = useState(false);
  
  // Stripe specific states
  const [stripePromise, setStripePromise] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [currentOrder, setCurrentOrder] = useState(null);

  // Location states
  const [shippingAddress, setShippingAddress] = useState('');
  const [shippingPoint, setShippingPoint] = useState({ longitude: 0.0 , latitude: 0.0 });
  const [tableId, setTableId] = useState(Number(localStorage.getItem('tableId')) || 0);
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [mapModalOpen, setMapModalOpen] = useState(false);
  const [isSelectMap, setIsSelectMap] = useState(false);
  const [location, setLocation] = useState({ lat: null, lng: null });
  const [selectedLocation, setSelectedLocation] = useState(null);
  
  // Points states
  const [isProcessing, setIsProcessing] = useState(false);
  const [usePoints, setUsePoints] = useState(false);
  const [userPoints, setUserPoints] = useState(0);
  const [pointsError, setPointsError] = useState('');
  const [pointsValueInt, setPointsValueInt] = useState(0.5);
  const [walletData, setWalletData] = useState([]);

  // Check location state
  const [isCheackLocation, SetIsCheackLocation] = useState(() => {
    const checkIsLocated = localStorage.getItem('is_check_location') === 'true';
    if(checkIsLocated) {
      try { 
        const savedLocation = JSON.parse(localStorage.getItem("user_location"));
        setShippingPoint({longitude:savedLocation.lng,latitude:savedLocation.lat});
      } catch(error) {
        return false;
      }
    }
    return checkIsLocated;
  });

  // Initialize query from saved location or table
  useEffect(() => {
    const savedLocation = JSON.parse(localStorage.getItem("user_location") || 'null');
    if(tableId) {
      setShippingAddress('Table number : '+ tableId);
    } else if(savedLocation) {
      setShippingAddress(savedLocation.address);
      setQuery(savedLocation.address);
    }
  }, [tableId]);

  const [openNoteInputs, setOpenNoteInputs] = useState({});
  const [itemNotes, setItemNotes] = useState({});
    
  const toggleNoteInput = (itemId) => {
    setOpenNoteInputs(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };
  
  const handleNoteChange = (itemId, note) => {
    setItemNotes(prev => ({
      ...prev,
      [itemId]: note
    }));
    
    if (updateItemNotes) {
      updateItemNotes(itemId, note);
    }
  };

  useEffect(() => {
    fetchPaymentMethods();
  }, []);

  const fetchPaymentMethods = async () => {
    try {
      const response = await api.get('/payments/methods');
      setPaymentMethods(response.data.methods || []);
      
      const stripeMethod = response.data.methods?.find(m => m.gateway === 'stripe');
      if (stripeMethod) {
        const stripeKey = stripeMethod.is_test_mode 
          ? 'pk_test_your_test_key'
          : stripeMethod.api_key;
        setStripePromise(loadStripe(stripeKey));
      }
    } catch (error) {
      console.error('Error fetching payment methods:', error);
    }
  };

  const fetchPlaces = async (input) => {
    if(isSelectMap) {
      return;
    }
    const response = await api.get(`/autocomplete?input=${input}&language=${language}`);
    const data = response.data;
    if (data.predictions) {
      setSuggestions(data.predictions.map((place) => place.description));
    }
  };

  const loadGoogleMapsAPI = (callback) => {
    if (window.google && window.google.maps) {
      callback();
      return;
    }
  
    if (!document.getElementById("google-maps-script")) {
      const script = document.createElement("script");
      const api = 'AIzaSyBl9P55wDymA5tQzFgWFaZWgMyQMdOSWXA';
      script.id = "google-maps-script";
      script.src = `https://maps.googleapis.com/maps/api/js?key=${api}&libraries=places`;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        console.log("Google Maps API Loaded");
        callback();
      };
      script.onerror = () => {
        console.error("Failed to load Google Maps API.");
      };
      document.body.appendChild(script);
    } else {
      setTimeout(() => {
        if (window.google && window.google.maps) {
          callback();
        }
      }, 1000);
    }
  };
    
  const handleLocationSelect = (locationM) => {
    if (!window.google || !window.google.maps) {
      loadGoogleMapsAPI(() => handleLocationSelect(locationM));
      return;
    }
    
    if(locationM.address) {
      setQuery(locationM.address);
      localStorage.setItem('user_location',JSON.stringify(locationM));
      setShippingPoint({longitude:locationM.lng,latitude:locationM.lat});
      setShippingAddress(locationM.address);
      SetIsCheackLocation(true);
      setIsSelectMap(true);
    } else {
      setQuery(locationM);
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ address: locationM }, (results, status) => {
        if (status === "OK" && results[0]) {
          const locationSet = results[0].geometry.location;
          setSelectedLocation({
            lat: locationSet.lat(),
            lng: locationSet.lng(),
            address: locationM,
          });
          setMapModalOpen(true);
        }
      });
    }
    setSuggestions([]);
  };

  const handleAutoDetect = () => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setSelectedLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        });
        setMapModalOpen(true);
      },
      (error) => {
        alert(language === 'ar' ? keywordsData.enable_gps_ar : keywordsData.enable_gps_en);
      }
    );
  };

  const handleQueryChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    setShippingAddress(value);
    
    clearTimeout(debounceTimeoutRef.current);
    debounceTimeoutRef.current = setTimeout(() => {
      if (value.length > 2) {
        fetchPlaces(value);
      } else {
        setSuggestions([]);
      }
    }, 500);
  };

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, []);

  useEffect(() => {
    const fetchUserPoints = async () => {
      if (user) {
        try {
          const response = await api.get('/payment/my-wallet');
          setUserPoints(response.data.points_balance || 0);
          setPointsValueInt(response.data.points_value_int || 0.5);
          setIsPhoneVerified(response.data.is_phone_verified || false);
          setWalletData(response.data);
        } catch (err) {
          console.error('Error fetching user points:', err);
        }
      }
    };

    fetchUserPoints();
  }, [user]);

// Alternative: Use useMemo for better performance
const pointsValues = useMemo(() => {
  if (!walletData?.points_value_int || walletData.points_value_int <= 0) {
    return {
      pointsValue: 0,
      maxPointsToUse: 0,
      pointsDiscount: 0,
      finalTotal: cartTotal
    };
  }

  const pointsValueInt = walletData.points_value_int;
  const calculatedMaxPointsToUse = Math.min(userPoints, Math.floor(cartTotal / pointsValueInt));
  const calculatedPointsDiscount = usePoints ? calculatedMaxPointsToUse * pointsValueInt : 0;
  const calculatedFinalTotal = Math.max(0, cartTotal - calculatedPointsDiscount);

  return {
    pointsValue: userPoints * pointsValueInt,
    maxPointsToUse: calculatedMaxPointsToUse,
    pointsDiscount: calculatedPointsDiscount,
    finalTotal: calculatedFinalTotal
  };
}, [userPoints, cartTotal, usePoints, walletData]);

  const pointsValue = pointsValues.pointsValue;
  const maxPointsToUse = pointsValues.maxPointsToUse;
  const pointsDiscount = pointsValues.pointsDiscount;
  const finalTotal = pointsValues.finalTotal ;

  const handlePointsChange = (event) => {
    setUsePoints(event.target.checked);
    setPointsError('');
  };

  const handlePaymentMethodChange = (event) => {
    setPaymentMethod(event.target.value);
    const method = paymentMethods.find(m => m.gateway === event.target.value);
    setSelectedPaymentMethod(method);
  };

  // Updated handleSubmit to show confirmation modal first
  const handleConfirmOrder = useCallback(async () => {
    setConfirmModalOpen(false);
    
    if (shippingAddress.length < 5) {
      alert(language === 'ar' ? 'الرجاء إدخال عنوان توصيل صالح' : 'Please enter a valid delivery address');
      return;
    }
    
    const orderItems = cart.map(item => ({
      product_id: item.product?.id,
      quantity: item.quantity,
      price: getItemPrice(item),
      notes: itemNotes[item.id] || item.notes || null,
      selected_size: item.selected_size || null,
      selected_extras: item.selected_extras || [],
      customization_price: item.customization_price || 0
    }));
    
    setIsProcessing(true);
    setPointsError('');

    if (usePoints && userPoints === 0) {
      setPointsError('You have no points to use');
      setIsProcessing(false);
      return;
    }

    try {
      const response = await api.post('/checkout', {
        shipping_address: shippingAddress,
        payment_method: paymentMethod,
        points_used: usePoints ? maxPointsToUse : 0,
        longitude: shippingPoint.longitude,
        latitude: shippingPoint.latitude,
        tableScan: tableId,
        items: orderItems,
        pax_no:localStorage.getItem('table_persons') || 0
      });

      if (paymentMethod !== 'cash_on_delivery') {
        setCurrentOrder(response.data);
        
        if (paymentMethod === 'stripe') {
          const paymentResponse = await api.post('/payments/process', {
            order_id: response.data.id,
            payment_method_id: selectedPaymentMethod.id,
            payment_details: {}
          });

          if (paymentResponse.data.clientSecret) {
            setClientSecret(paymentResponse.data.clientSecret);
            setPaymentDialogOpen(true);
          }
        }
      } else {
        clearCart();
        navigate(`/order-confirmation/${response.data.id}`);
      }
      
    } catch (err) {
      console.error('Checkout failed:', err);
      if (err.response?.data?.message === 'Insufficient points balance') {
        setPointsError('Insufficient points balance');
      } else if (err.response?.data?.message === 'Pharmacy closed'){
        alert(language === 'ar' ? 'الكافي مغلق في الوقت الحالي الرجاء المحاولة لاحقا' : 'The cafe is currently closed. Please try again later.');
      } else {
        alert('Checkout failed. Please try again.');
      }
    } finally {
      setIsProcessing(false);
    }
  }, [
    shippingAddress, language, cart, getItemPrice, itemNotes, usePoints, 
    userPoints, maxPointsToUse, paymentMethod, shippingPoint, tableId, 
    selectedPaymentMethod, clearCart, navigate, setPointsError
  ]);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate before showing modal
    if (shippingAddress.length < 5) {
      alert(language === 'ar' ? 'الرجاء إدخال عنوان توصيل صالح' : 'Please enter a valid delivery address');
      return;
    }
    
    if (cart.length === 0) {
      alert(language === 'ar' ? 'السلة فارغة' : 'Cart is empty');
      return;
    }
    
    if (finalTotal <= 0 && !usePoints) {
      alert(language === 'ar' ? 'يرجى استخدام النقاط للدفع' : 'Please use points to pay');
      return;
    }
    
    // Show confirmation modal
    setConfirmModalOpen(true);
  };

  const handleStripeSuccess = async (paymentIntent) => {
    setPaymentResult({
      success: true,
      transaction: {
        transaction_id: paymentIntent.id,
        amount: paymentIntent.amount / 100
      },
      order: currentOrder
    });
    
    clearCart();
    setTimeout(() => {
      navigate(`/order-confirmation/${currentOrder.id}?payment=success`);
    }, 2000);
  };

  const handleStripeError = (error) => {
    setPaymentResult({
      success: false,
      error: error
    });
  };

  const carancyTxt = language === 'ar' ? keywordsData.carance_txt_ar : keywordsData.carance_txt_en;
  const carrancySet = (amount) => {
    return formatPrice(amount,cartCurrency,language);
  };
  const prossisingTxt = language === 'ar' ? 'جار المعالجة ...' : 'Processing ...';
  const contenuWithPointsTxt = language === 'ar' ? 'أكمل الطلب بالنقاط' : 'Complete with points' ;
  const orderTxt = language === 'ar' ? 'إطلب ' : 'Order ';
  const saveTxt = language === 'ar' ? ' و وفر ' : ' and save ';
  
  const totalWithFees = selectedPaymentMethod && paymentMethod !== 'cash_on_delivery'
    ? finalTotal + (finalTotal * (selectedPaymentMethod.fee_percent || 0) / 100)
    : finalTotal;

  const originalTotal = cart.reduce((total, item) => {
    return total + (getOriginalPrice(item) * item.quantity);
  }, 0);

  // Calculate final display total
  const displayTotal = paymentMethod !== 'cash_on_delivery' && selectedPaymentMethod
    ? (finalTotal + (finalTotal * selectedPaymentMethod.fee_percent / 100))
    : finalTotal;

  // Check if button should be sticky
  const [isSticky, setIsSticky] = useState(false);
  
  useEffect(() => {
    const checkScreenSize = () => {
      const isMobileScreen = window.innerWidth < 768;
      
      if (isMobileScreen) {
        setIsSticky(true);
      } else {
        const scrollPosition = window.scrollY + window.innerHeight;
        const documentHeight = document.documentElement.scrollHeight;
        setIsSticky(scrollPosition < documentHeight - 200);
      }
    };
    
    checkScreenSize();
    
    const handleScroll = () => {
      if (window.innerWidth >= 768) {
        const scrollPosition = window.scrollY + window.innerHeight;
        const documentHeight = document.documentElement.scrollHeight;
        setIsSticky(scrollPosition < documentHeight - 200);
      }
    };
    
    const handleResize = () => {
      checkScreenSize();
    };
    
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Calculate items count
  const totalItems = cart.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <Container maxWidth="md" sx={{ 
      py: { xs: 2, sm: 4 }, 
      mb: { xs: 8, sm: 10 },
      backgroundColor: '#000000',
      minHeight: '100vh',
      color: '#FFFFFF',
      pb: { xs: 12, sm: 10 }
    }}>
      <Typography variant="h4" gutterBottom sx={{ 
        color: '#FFD700', 
        fontWeight: 800,
        borderBottom: '3px solid #FFD700',
        pb: 1,
        mb: 3,
        display: 'inline-block'
      }}>
        {language === "ar" ? keywordsData.check_out_title_txt_ar : keywordsData.check_out_title_txt_en}
      </Typography>

      <Paper elevation={3} sx={{ 
        p: 3, 
        backgroundColor: '#0D0D0D',
        border: '1px solid #2D2D2D',
        borderRadius: '16px',
        '&:hover': {
          border: '1px solid #FFD700',
        }
      }}>
        <form onSubmit={handleSubmit}>
          {/* Delivery Address Section */}
          <div ref={contentRef}>
            <Box sx={{ mb: 4 }}>
              <FormLabel component="legend" sx={{ 
                color: '#FFD700', 
                fontWeight: 600,
                mb: 1.5,
                fontSize: { xs: '0.9rem', sm: '1rem' },
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}>
                <LocationOn sx={{ fontSize: { xs: 18, sm: 20 } }} />
                {language === "ar" ? keywordsData.deliver_adress_txt_ar : keywordsData.deliver_adress_txt_en}
              </FormLabel>
              
              {tableId === 0 ? (
                <Box sx={{ position: 'relative' }}>
                  <Box sx={{ display: 'flex', gap: { xs: 1, sm: 2 }, flexDirection: { xs: 'column', sm: 'row' } }}>
                    <Box sx={{ flexGrow: 1, position: 'relative' }}>
                      <TextField
                        fullWidth
                        variant="outlined"
                        placeholder={language === "ar" ? keywordsData.search_match_location_place_holder_ar : keywordsData.search_match_location_place_holder_en}
                        value={query}
                        onChange={(e) => handleQueryChange(e)}
                        required
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <LocationOn sx={{ color: '#FFD700', fontSize: 20 }} />
                            </InputAdornment>
                          ),
                          sx: {
                            backgroundColor: '#1A1A1A',
                            borderRadius: '50px',
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#2D2D2D',
                              borderWidth: '1px',
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#FFD700',
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#FFD700',
                              borderWidth: '2px',
                            },
                            '& input': {
                              color: '#FFFFFF',
                              padding: { xs: '12px 14px', sm: '14px 16px' },
                              fontSize: { xs: '14px', sm: '16px' },
                            },
                          },
                        }}
                      />
                      
                      <Collapse in={suggestions.length > 0} timeout={200}>
                        <Paper elevation={3} sx={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          right: 0,
                          backgroundColor: '#0D0D0D',
                          border: '1px solid #FFD700',
                          borderRadius: '16px',
                          mt: 1,
                          zIndex: 1000,
                          maxHeight: '320px',
                          overflowY: 'auto',
                          boxShadow: '0 8px 32px rgba(255, 215, 0, 0.15)',
                          '&::-webkit-scrollbar': {
                            width: '6px',
                          },
                          '&::-webkit-scrollbar-track': {
                            backgroundColor: '#1A1A1A',
                            borderRadius: '3px',
                          },
                          '&::-webkit-scrollbar-thumb': {
                            backgroundColor: '#FFD700',
                            borderRadius: '3px',
                          },
                        }}>
                          {suggestions.map((place, index) => (
                            <Box
                              key={index}
                              onClick={() => handleLocationSelect(place)}
                              sx={{
                                p: { xs: 1.5, sm: 2 },
                                cursor: 'pointer',
                                color: '#FFFFFF',
                                borderBottom: index < suggestions.length - 1 ? '1px solid #2D2D2D' : 'none',
                                transition: 'all 0.2s ease',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5,
                                '&:hover': {
                                  backgroundColor: '#FFD700',
                                  color: '#000000',
                                  transform: 'translateX(4px)',
                                },
                              }}
                            >
                              <LocationOn sx={{ fontSize: 18, color: '#FFD700' }} />
                              <Typography variant="body2" sx={{ flex: 1 }}>
                                {place}
                              </Typography>
                            </Box>
                          ))}
                        </Paper>
                      </Collapse>
                    </Box>
                    
                    <Button 
                      variant="outlined" 
                      onClick={handleAutoDetect}
                      startIcon={<MyLocationIcon />}
                      sx={{
                        borderColor: '#FFD700',
                        color: '#FFD700',
                        borderRadius: '50px',
                        px: { xs: 2, sm: 3 },
                        py: { xs: 1, sm: 1.2 },
                        minWidth: { xs: 'auto', sm: '120px' },
                        whiteSpace: 'nowrap',
                        '&:hover': {
                          backgroundColor: '#FFD700',
                          color: '#000000',
                          borderColor: '#FFD700',
                          transform: 'translateY(-2px)',
                        },
                        transition: 'all 0.2s ease',
                        fontWeight: 600,
                        fontSize: { xs: '0.8rem', sm: '0.875rem' },
                      }}
                    >
                      <Typography sx={{ display: { xs: 'none', sm: 'block' } }}>
                        {language === "ar" ? "تحديد موقعي" : "My Location"}
                      </Typography>
                    </Button>
                  </Box>
                </Box>
              ) : (
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder={language === "ar" ? keywordsData.search_match_location_place_holder_ar : keywordsData.search_match_location_place_holder_en}
                  value={`Table number : ${tableId}`}
                  disabled
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LocationOn sx={{ color: '#FFD700', fontSize: 20 }} />
                      </InputAdornment>
                    ),
                    sx: {
                      backgroundColor: '#1A1A1A',
                      borderRadius: '50px',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#2D2D2D',
                      },
                      '& input': {
                        color: '#6B6B6B',
                        padding: { xs: '12px 14px', sm: '14px 16px' },
                        fontSize: { xs: '14px', sm: '16px' },
                      },
                    },
                  }}
                />
              )}
            </Box>

            {/* Payment Method Section */}
            <Box sx={{ mb: 4 }}>
              <FormLabel component="legend" sx={{ 
                color: '#FFD700', 
                fontWeight: 600,
                mb: 1,
                fontSize: '1rem'
              }}>
                {language === "ar" ? keywordsData.payment_method_txt_ar : keywordsData.payment_method_txt_en}
              </FormLabel>
              <RadioGroup
                value={paymentMethod}
                onChange={handlePaymentMethodChange}
              >
                <FormControlLabel
                  value="cash_on_delivery"
                  control={<Radio sx={{
                    color: '#2D2D2D',
                    '&.Mui-checked': {
                      color: '#FFD700',
                    },
                  }} />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography sx={{ color: '#FFFFFF' }}>
                        {language === "ar" ? keywordsData.payment_cash_txt_ar : keywordsData.payment_cash_txt_en}
                      </Typography>
                    </Box>
                  }
                />

                {paymentMethods.map((method) => (
                  <FormControlLabel
                    key={method.id}
                    value={method.gateway}
                    control={<Radio sx={{
                      color: '#2D2D2D',
                      '&.Mui-checked': {
                        color: '#FFD700',
                      },
                    }} />}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <span style={{ fontSize: '1.5rem' }}>{method.icon}</span>
                          <Typography sx={{ color: '#FFFFFF' }}>
                            {language === 'ar' ? method.name_ar : method.name}
                          </Typography>
                        </Box>
                        {method.is_test_mode && (
                          <Chip
                            label={language === 'ar' ? 'وضع الاختبار' : 'Test Mode'}
                            size="small"
                            sx={{ backgroundColor: '#FFD700', color: '#000000' }}
                          />
                        )}
                      </Box>
                    }
                  />
                ))}
              </RadioGroup>

              {paymentMethod !== 'cash_on_delivery' && selectedPaymentMethod && (
                <Alert 
                  severity="info"
                  sx={{ 
                    mt: 2,
                    backgroundColor: 'rgba(255, 215, 0, 0.1)',
                    color: '#FFFFFF',
                    border: '1px solid #FFD700'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LockIcon sx={{ color: '#FFD700' }} />
                    <Typography variant="body2">
                      {language === 'ar' 
                        ? paymentMethod === 'stripe'
                          ? 'مدعوم من Stripe - معلومات بطاقتك آمنة ومشفرة'
                          : 'معلومات بطاقتك مشفرة وآمنة 100%'
                        : paymentMethod === 'stripe'
                          ? 'Powered by Stripe - Your card information is secure and encrypted'
                          : 'Your card information is 100% encrypted and secure'
                      }
                    </Typography>
                  </Box>
                </Alert>
              )}
            </Box>

            {/* Points Redemption Section */}
            {userPoints > 0 && (
              <Box sx={{ 
                mb: 4, 
                p: 3, 
                backgroundColor: '#0D0D0D', 
                border: '1px solid #2D2D2D',
                borderRadius: '12px',
                opacity: !isPhoneVerified ? 0.7 : 1,
                '&:hover': {
                  border: !isPhoneVerified ? '1px solid #2D2D2D' : '1px solid #FFD700',
                }
              }}>
                <Typography variant="h6" gutterBottom sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1,
                  color: '#FFD700',
                  fontWeight: 700
                }}>
                  <Loyalty sx={{ color: '#FFD700' }} /> {language === "ar" ? keywordsData.point_earned_txt_ar : keywordsData.point_earned_txt_en}
                  
                  {!isPhoneVerified && (
                    <Chip
                      icon={<PhoneIcon />}
                      label={language === 'ar' ? 'يجب توثيق رقم الهاتف أولاً' : 'Phone verification required'}
                      size="small"
                      sx={{
                        backgroundColor: '#FF9800',
                        color: '#000000',
                        fontWeight: 600,
                        ml: 1
                      }}
                    />
                  )}
                </Typography>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Checkbox
                    checked={usePoints}
                    onChange={handlePointsChange}
                    disabled={!isPhoneVerified}
                    sx={{
                      color: '#2D2D2D',
                      '&.Mui-checked': {
                        color: '#FFD700',
                      },
                    }}
                  />
                  <Box>
                    <Typography variant="body1" fontWeight="medium" sx={{ 
                      color: !isPhoneVerified ? '#6B6B6B' : '#FFFFFF' 
                    }}>
                      {language === "ar" ? keywordsData.use_point_earned_txt_ar : keywordsData.use_point_earned_txt_en}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#B3B3B3' }}>
                      {language === "ar" ? keywordsData.you_have_txt_ar : keywordsData.you_have_txt_en} {userPoints}  {language === "ar" ? keywordsData.points_txt_ar : keywordsData.points_txt_en} ({carrancySet(pointsValue.toFixed(2))})
                    </Typography>
                  </Box>
                </Box>

                {usePoints && (
                  <Box sx={{ ml: 6 }}>
                    <Alert 
                      severity="info" 
                      icon={<InfoIcon />} 
                      sx={{ 
                        mb: 2,
                        backgroundColor: '#1A1A1A',
                        color: '#FFFFFF',
                        border: '1px solid #FFD700',
                      }}
                    >
                      <Typography variant="body2" sx={{ color: '#FFFFFF' }}>
                        {language === "ar" ? ' إستعمال' : 'Use '} {maxPointsToUse} {language === "ar" ? keywordsData.points_txt_ar : keywordsData.points_txt_en} ({carrancySet(pointsDiscount.toFixed(2))}) 
                        {language === "ar" ? ' من الرصيد المتاح' : ' From available balance'}
                      </Typography>
                    </Alert>
                    
                    <Typography variant="body2" sx={{ color: '#B3B3B3' }}>
                     {language === 'ar' ? ' سيتم تطبيق النقاط كما يلي:' : 'Points will be applied as follow: '} <strong style={{ color: '#FFD700' }}>{carrancySet(pointsDiscount.toFixed(2))} {language === "ar" ? 'تخفيض' : 'Discount'}</strong>
                    </Typography>
                    
                    <Typography variant="caption" display="block" sx={{ color: '#6B6B6B', mt: 1 }}>
                      {language === 'ar' ?  `* قيمة كل نقطة ${carrancySet(pointsValueInt)}. الحد الأقصى للنقاط المطبقة:  ` :  `* Each point is worth ${carrancySet(pointsValueInt)}. Maximum number of points applied:  `} {maxPointsToUse}
                    </Typography>
                  </Box>
                )}

                {pointsError && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    {pointsError}
                  </Alert>
                )}

                {!isPhoneVerified && (
                  <Alert 
                    severity="warning"
                    icon={<PhoneIcon />}
                    sx={{ 
                      mt: 2,
                      backgroundColor: 'rgba(255, 152, 0, 0.1)',
                      color: '#FF9800',
                      border: '1px solid #FF9800',
                    }}
                  >
                    <Typography variant="body2">
                      {language === 'ar' 
                        ? 'يجب توثيق رقم هاتفك أولاً لتتمكن من استخدام رصيد النقاط' 
                        : 'You need to verify your phone number first to use points balance'}
                    </Typography>
                    <Button
                      size="small"
                      onClick={() => navigate('../account')}
                      sx={{
                        mt: 1,
                        color: '#FF9800',
                        borderColor: '#FF9800',
                      }}
                      variant="outlined"
                    >
                      {language === 'ar' ? 'توثيق الآن' : 'Verify Now'}
                    </Button>
                  </Alert>
                )}
              </Box>
            )}

            <Divider sx={{ my: 2, backgroundColor: '#2D2D2D' }} />

            {/* Order Summary */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" gutterBottom sx={{ 
                color: '#FFD700',
                fontWeight: 700,
                mb: 2
              }}>
                {language === 'ar' ? 'ملخص الطلب' : 'Order Summary'}
              </Typography>
              
              {/* Savings Banner */}
              {totalSavings > 0 && (
                <Box sx={{ 
                  backgroundColor: '#0D0D0D',
                  border: '1px solid #FFD700',
                  p: 2, 
                  borderRadius: '12px', 
                  mb: 3,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}>
                  <Savings sx={{ color: '#FFD700' }} />
                  <Box>
                    <Typography variant="subtitle1" sx={{ 
                      color: '#FFD700', 
                      fontWeight: 'bold'
                    }}>
                     {language === 'ar' ? 'لقد وفرت ' : 'You save '} {carrancySet(totalSavings.toFixed(2))} {language === 'ar' ? 'على هاذا الطلب!' : 'on this order!'}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#B3B3B3' }}>
                      {language === 'ar' ? 'عروض رائعة على المنتجات المختارة' : 'Great offers on selected products'}
                    </Typography>
                  </Box>
                </Box>
              )}

              <List sx={{ 
                backgroundColor: '#0D0D0D',
                borderRadius: '12px',
                p: 2
              }}>
                {cart.map((item) => {
                  const currentPrice = getItemPrice(item);
                  const originalPrice = getOriginalPrice(item);
                  const isDiscounted = hasDiscount(item);
                  const discountPercent = isDiscounted 
                    ? Math.round((1 - currentPrice / originalPrice) * 100)
                    : 0;
                  
                  const itemNote = itemNotes[item.id] || item.notes || '';
                  const isNoteOpen = openNoteInputs[item.id] || false;

                  return (
                    <ListItem key={item.id} sx={{ 
                      alignItems: 'flex-start',
                      flexDirection: 'column',
                      borderBottom: '1px solid #2D2D2D',
                      '&:last-child': {
                        borderBottom: 'none',
                      }
                    }}>
                      <Box sx={{ display: 'flex', width: '100%' }}>
                        <Box sx={{ 
                          mr: 2, 
                          width: 60, 
                          height: 60,
                          backgroundColor: '#1A1A1A',
                          borderRadius: '8px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0
                        }}>
                          <img
                            src={item.product?.imageUrl || noPhoto}
                            alt={item.product?.name}
                            style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: '8px' }}
                          />
                        </Box>
                        
                        <Box sx={{ flexGrow: 1 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <Box>
                              <Typography variant="subtitle2" fontWeight="medium" sx={{ color: '#FFFFFF' }}>
                                {language === 'ar' ? item.product?.name : item.product?.name_en}&nbsp;
                              </Typography>
                              
                              {isDiscounted ? (
                                <Box>
                                  <Typography variant="body2" sx={{ 
                                    color: '#FFD700', 
                                    fontWeight: 'bold',
                                    display: 'inline-block',
                                    ml: 1
                                  }}>
                                    {carrancySet(currentPrice.toFixed(2))} × {item.quantity}
                                  </Typography>
                                  <Typography 
                                    variant="caption" 
                                    sx={{ 
                                      textDecoration: 'line-through', 
                                      color: '#6B6B6B',
                                      display: 'block'
                                    }}
                                  >
                                    {carrancySet(originalPrice.toFixed(2))} {language === "ar" ? 'كل' : 'each'}
                                  </Typography>
                                  <Chip
                                    label={language === "ar" ? `وفر ${discountPercent}%` : `Save ${discountPercent}%`}
                                    size="small"
                                    sx={{
                                      mt: 0.5,
                                      backgroundColor: '#FFD700',
                                      color: '#000000',
                                      fontWeight: 'bold',
                                      height: 20,
                                      fontSize: '0.7rem'
                                    }}
                                  />
                                </Box>
                              ) : (
                                <Typography variant="body2" sx={{ color: '#B3B3B3' }}>
                                  {carrancySet(currentPrice.toFixed(2))} × {item.quantity}
                                </Typography>
                              )}
                            </Box>
                            
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <IconButton 
                                size="small" 
                                onClick={() => toggleNoteInput(item.id)}
                                sx={{ color: itemNote ? '#FFD700' : '#B3B3B3' }}
                              >
                                <NoteAddIcon fontSize="small" />
                              </IconButton>
                              <Typography variant="subtitle2" fontWeight="bold" sx={{ 
                                color: '#FFD700',
                                whiteSpace: 'nowrap'
                              }}>
                                {carrancySet((currentPrice * item.quantity).toFixed(2))}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                      
                      <Collapse in={isNoteOpen} sx={{ width: '100%', mt: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, pl: 8 }}>
                          <TextField
                            fullWidth
                            size="small"
                            multiline
                            rows={2}
                            helperText={language === 'ar' ? 'إنجليزي فقط' : 'English only'}
                            placeholder={language === "ar" ? "ملاحظات إضافية (مثل: بدون بصل، إضافي جبن...)" : "Additional notes (e.g., no onion, extra cheese...)"}
                            value={itemNote}
                            onChange={(e) => handleNoteChange(item.id, e.target.value)}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                backgroundColor: '#1A1A1A',
                                color: '#FFFFFF',
                                '& fieldset': { borderColor: '#2D2D2D' },
                                '&:hover fieldset': { borderColor: '#FFD700' },
                              }
                            }}
                          />
                          {itemNote && (
                            <IconButton 
                              size="small" 
                              onClick={() => handleNoteChange(item.id, '')}
                              sx={{ color: '#B3B3B3' }}
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                      </Collapse>
                    </ListItem>
                  );
                })}
              </List>
              
              {/* Order Totals */}
              <Box sx={{ 
                mt: 2,
                p: 2,
                backgroundColor: '#0D0D0D',
                border: '1px solid #2D2D2D',
                borderRadius: '12px'
              }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography sx={{ color: '#B3B3B3' }}> {language === "ar" ? keywordsData.deliver_fees_txt_ar : keywordsData.deliver_fees_txt_en} :</Typography>
                  <Typography sx={{ color: '#FFFFFF', fontWeight: 600 }}>
                    {carrancySet(deliverFees.toFixed(2))}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography sx={{ color: '#B3B3B3' }}> {language === 'ar' ? 'رسوم الخدمة ' : 'Service charge '} {serviceChargePercent.toFixed(2)}% :</Typography>
                  <Typography sx={{ color: '#FFFFFF', fontWeight: 600 }}>
                    {carrancySet(serviceChargeValue.toFixed(2))}
                  </Typography>
                </Box>

                {paymentMethod !== 'cash_on_delivery' && selectedPaymentMethod && selectedPaymentMethod.fee_percent > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography sx={{ color: '#B3B3B3' }}>
                      {language === 'ar' ? 'رسوم المعاملة' : 'Transaction Fee'} ({selectedPaymentMethod.fee_percent}%):
                    </Typography>
                    <Typography sx={{ color: '#FFD700', fontWeight: 600 }}>
                      +{carrancySet((finalTotal * selectedPaymentMethod.fee_percent / 100).toFixed(2))}
                    </Typography>
                  </Box>
                )}

                {totalSavings > 0 && (
                  <>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" sx={{ color: '#B3B3B3' }}>
                        {language === "ar" ? 'السعر الإساسي :' : 'Origenal price :'}
                      </Typography>
                      <Typography variant="body2" sx={{ textDecoration: 'line-through', color: '#6B6B6B' }}>
                       {carrancySet(originalTotal.toFixed(2))}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" sx={{ color: '#FFD700' }}>
                        <LocalOffer fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5, color: '#FFD700' }} />
                        {language === "ar" ? 'تخفيض :' : 'Discount :'}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#FFD700' }}>
                        - {carrancySet(totalSavings.toFixed(2))}
                      </Typography>
                    </Box>
                  </>
                )}

                {usePoints && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" sx={{ color: '#FFD700' }}>
                      <Loyalty fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5, color: '#FFD700' }} />
                      {language === "ar" ? 'تخفيض النقاط :' : 'Points Discount :'}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#FFD700' }}>
                      - {carrancySet(pointsDiscount.toFixed(2))}
                    </Typography>
                  </Box>
                )}

                {(totalSavings > 0 || usePoints || (paymentMethod !== 'cash_on_delivery' && selectedPaymentMethod?.fee_percent > 0)) && (
                  <Divider sx={{ my: 1, backgroundColor: '#2D2D2D' }} />
                )}
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="h6" sx={{ color: '#FFFFFF', fontWeight: 700 }}>
                    {language === "ar" ? 'الإجمالي :' : 'Total :'}
                  </Typography>
                  <Typography 
                    variant="h5" 
                    sx={{ 
                      color: '#FFD700',
                      fontWeight: 900,
                      fontSize: '1.5rem'
                    }}
                  >
                    {carrancySet(displayTotal.toFixed(2))}
                  </Typography>
                </Box>

                {(totalSavings > 0 || usePoints) && (
                  <Typography variant="body2" sx={{ 
                    color: '#FFD700', 
                    textAlign: 'right', 
                    mt: 1,
                    fontWeight: 600
                  }}>
                   {language === "ar" ? 'قيمة التخفيض: ' : 'Discount value: '} {carrancySet((totalSavings + pointsDiscount).toFixed(2))}
                  </Typography>
                )}
              </Box>

              <Typography variant="body2" sx={{ 
                color: '#6B6B6B', 
                mt: 2, 
                textAlign: 'center' 
              }}>
                {totalItems} {language === "ar" ? 'منتج في طلبك' : 'Product'}
              </Typography>
            </Box>

            {/* Sticky Submit Button */}
            <Zoom in={isSticky} timeout={300}>
              <Paper
                elevation={6}
                sx={{
                  position: 'fixed',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  zIndex: 1100,
                  backgroundColor: '#0D0D0D',
                  borderTop: '2px solid #FFD700',
                  borderRadius: 0,
                  p: { xs: 1.5, sm: 2 },
                  marginBottom: 7,
                  display: { xs: isSticky ? 'block' : 'none', sm: 'none' }
                }}
              >
                <Box sx={{ maxWidth: 'md', mx: 'auto' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2" sx={{ color: '#B3B3B3' }}>
                      {totalItems} {language === "ar" ? 'منتج' : 'items'}
                    </Typography>
                    <Typography variant="h6" sx={{ color: '#FFD700', fontWeight: 800 }}>
                      {carrancySet(displayTotal.toFixed(2))}
                    </Typography>
                  </Box>
                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    disabled={isProcessing || cart.length === 0 || finalTotal <= 0}
                    sx={{
                      backgroundColor: '#FFD700',
                      color: '#000000',
                      fontWeight: 800,
                      fontSize: { xs: '1rem', sm: '1.1rem' },
                      py: 1.5,
                      borderRadius: '12px',
                      '&:hover': {
                        backgroundColor: '#FFE55C',
                        boxShadow: '0 4px 12px rgba(255, 215, 0, 0.4)',
                      },
                    }}
                  >
                    {isProcessing ? prossisingTxt : (
                      finalTotal === 0 ? contenuWithPointsTxt :
                      `${orderTxt} ${(totalSavings > 0 || usePoints) ? `${saveTxt} ${carrancySet((totalSavings + pointsDiscount).toFixed(2))}` : ''}`
                    )}
                  </Button>
                </Box>
              </Paper>
            </Zoom>

            {/* Original Submit Button */}
            <Button
              type="submit"
              variant="contained"
              size="large"
              fullWidth
              disabled={isProcessing || cart.length === 0 || finalTotal <= 0}
              sx={{
                backgroundColor: '#FFD700',
                color: '#000000',
                fontWeight: 800,
                fontSize: { xs: '1rem', sm: '1.1rem' },
                py: 1.8,
                borderRadius: '12px',
                display: { xs: isSticky ? 'none' : 'flex', sm: 'flex' },
                '&:hover': {
                  backgroundColor: '#FFE55C',
                  boxShadow: '0 8px 20px rgba(255, 215, 0, 0.4)',
                },
                '&.Mui-disabled': {
                  backgroundColor: '#2D2D2D',
                  color: '#6B6B6B',
                }
              }}
            >
              {isProcessing ? prossisingTxt : (
                finalTotal === 0 ? contenuWithPointsTxt :
                `${orderTxt} ${(totalSavings > 0 || usePoints) ? `${saveTxt} ${carrancySet((totalSavings + pointsDiscount).toFixed(2))}` : ''}`
              )}
            </Button>
            
            {finalTotal === 0 && (
              <Alert 
                severity="info" 
                sx={{ 
                  mt: 2,
                  backgroundColor: '#0D0D0D',
                  color: '#FFD700',
                  border: '1px solid #FFD700',
                }}
              >
                {language === 'ar' ? 'سيتم دفع طلبك بالكامل باستخدام نقاطك المكتسبة!' : 'Your order will be fully paid for using your earned points!'}
              </Alert>
            )}
          </div>
        </form>
      </Paper>

      {/* Confirmation Timer Modal */}
<ConfirmationTimerModal
  open={confirmModalOpen}
  onClose={() => setConfirmModalOpen(false)}
  onConfirm={handleConfirmOrder}
  itemsCount={totalItems}
  totalAmount={displayTotal}
  language={language}
  carancyTxt={cartCurrency}
  savingsAmount={totalSavings}
  pointsDiscount={pointsDiscount}
  cartItems={cart} // Pass cart items
  getItemPrice={getItemPrice} // Pass the function
  getOriginalPrice={getOriginalPrice} // Pass the function
  hasDiscount={hasDiscount} // Pass the function
/>

      {/* Stripe Payment Dialog */}
      <Dialog 
        open={paymentDialogOpen} 
        onClose={() => setPaymentDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: '#0D0D0D',
            border: '1px solid #FFD700',
            borderRadius: '16px'
          }
        }}
      >
        <DialogTitle sx={{ color: '#FFD700', fontWeight: 700, borderBottom: '1px solid #2D2D2D' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CreditCardIcon />
            <Typography>
              {language === 'ar' ? 'إدخال بيانات البطاقة' : 'Enter Card Details'}
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          {stripePromise && clientSecret && (
            <Elements stripe={stripePromise} options={{ clientSecret }}>
              <StripePaymentForm
                clientSecret={clientSecret}
                onSuccess={handleStripeSuccess}
                onError={handleStripeError}
                onCancel={() => setPaymentDialogOpen(false)}
                language={language}
                totalAmount={displayTotal.toFixed(2)}
              />
            </Elements>
          )}
        </DialogContent>
      </Dialog>

      {/* Payment Result Dialog */}
      <Dialog
        open={!!paymentResult}
        onClose={() => setPaymentResult(null)}
        PaperProps={{
          sx: {
            backgroundColor: '#0D0D0D',
            border: `1px solid ${paymentResult?.success ? '#4CAF50' : '#FF3D00'}`,
            borderRadius: '16px'
          }
        }}
      >
        <DialogContent sx={{ textAlign: 'center', p: 4 }}>
          {paymentResult?.success ? (
            <>
              <CheckCircleIcon sx={{ fontSize: 60, color: '#4CAF50', mb: 2 }} />
              <Typography variant="h5" sx={{ color: '#4CAF50', fontWeight: 700, mb: 1 }}>
                {language === 'ar' ? 'تم الدفع بنجاح!' : 'Payment Successful!'}
              </Typography>
              <Typography variant="body2" sx={{ color: '#B3B3B3' }}>
                {language === 'ar' 
                  ? `رقم المعاملة: ${paymentResult.transaction?.transaction_id}`
                  : `Transaction ID: ${paymentResult.transaction?.transaction_id}`
                }
              </Typography>
            </>
          ) : (
            <>
              <ErrorIcon sx={{ fontSize: 60, color: '#FF3D00', mb: 2 }} />
              <Typography variant="h5" sx={{ color: '#FF3D00', fontWeight: 700, mb: 1 }}>
                {language === 'ar' ? 'فشل الدفع' : 'Payment Failed'}
              </Typography>
              <Typography variant="body2" sx={{ color: '#B3B3B3' }}>
                {paymentResult?.error}
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', p: 3 }}>
          <Button
            variant="contained"
            onClick={() => {
              setPaymentResult(null);
              setPaymentDialogOpen(false);
              if (paymentResult?.success) {
                navigate(`/order-confirmation/${currentOrder?.id}?payment=success`);
              }
            }}
            sx={{
              backgroundColor: '#FFD700',
              color: '#000000',
              '&:hover': { backgroundColor: '#FFE55C' }
            }}
          >
            {language === 'ar' ? 'متابعة' : 'Continue'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Google Maps Modal */}
      <GoogleMapModal 
        open={mapModalOpen} 
        handleClose={() => setMapModalOpen(false)} 
        onLocationSelect={handleLocationSelect} 
        initialCenter={selectedLocation} 
        language={language} 
        keywordsData={keywordsData}
      />
    </Container>
  );
};

export default CheckoutPage;